import React from "react";
import { Box, Tooltip, Typography, styled } from "@mui/material";
import type { CareerCluster } from "../../types";

interface ClusterChipProps {
  cluster: CareerCluster;
  /** When true, chip is constrained to ~50% width with text truncation */
  truncate?: boolean;
  onClick?: (clusterId: string) => void;
}

const ChipContainer = styled(Box, {
  label: "ClusterChipContainer",
  shouldForwardProp: (prop) => prop !== "truncate" && prop !== "clickable",
})<{ truncate?: boolean; clickable?: boolean }>(({ truncate, clickable }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "4px 8px 4px 4px",
  borderRadius: 10,
  border: "1px solid rgba(255, 255, 255, 0.64)",
  height: 32,
  boxSizing: "border-box",
  ...(truncate && {
    maxWidth: "calc(50% - 4px)",
  }),
  ...(clickable && {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.12)",
    },
  }),
}));

const ClusterName = styled(Typography, {
  label: "ClusterChipName",
})<{ truncate?: boolean }>(({ truncate }) => ({
  fontFamily: "Proxima Nova",
  fontSize: "13px",
  fontWeight: 500,
  color: "#ffffff",
  lineHeight: "18px",
  ...(truncate && {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    flex: 1,
  }),
}));

export const ClusterChip = ({
  cluster,
  truncate,
  onClick,
}: ClusterChipProps) => {
  const chip = (
    <ChipContainer
      truncate={truncate}
      clickable={!!onClick}
      onClick={onClick ? () => onClick(cluster.id) : undefined}
    >
      {cluster.icon?.url && (
        <img
          src={cluster.icon.url}
          alt=""
          width={24}
          height={24}
          style={{ objectFit: "contain", flexShrink: 0 }}
        />
      )}
      <ClusterName truncate={truncate}>{cluster.name}</ClusterName>
    </ChipContainer>
  );

  if (truncate) {
    return <Tooltip title={cluster.name}>{chip}</Tooltip>;
  }

  return chip;
};
